import { useEffect, useMemo, useState, useCallback } from "react";
import { Loader2, Users2, Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DISC_LABEL = { artistica: "Ginnastica Artistica", ritmica: "Ginnastica Ritmica" };
const TYPE_LABEL = { coppia: "Coppia", squadra: "Squadra", combinata: "Combinata" };
const TYPES = ["coppia", "squadra", "combinata"];
const MANAGER_ROLES = ["admin", "segreteria"];
const emptyForm = { id: null, name: "", group_type: "squadra", discipline: "artistica", category: "", member_ids: [], event_id: "" };

export default function GroupsManager() {
  const { user } = useAuth();
  const isManager = MANAGER_ROLES.includes(user?.role);

  const [groups, setGroups] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadGroups = useCallback(() => api.get("/groups").then((r) => setGroups(r.data)).catch(() => {}), []);

  useEffect(() => {
    Promise.all([
      loadGroups(),
      api.get("/athletes").then((r) => setAthletes(r.data)).catch(() => {}),
      api.get("/events").then((r) => setEvents(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [loadGroups]);

  const eligibleAthletes = useMemo(
    () => athletes.filter((a) => a.discipline === form.discipline),
    [athletes, form.discipline]
  );

  const openCreate = () => { setForm(emptyForm); setShowForm(true); };
  const openEdit = (g) => {
    setForm({ id: g.id, name: g.name, group_type: g.group_type, discipline: g.discipline, category: g.category || "", member_ids: g.member_ids || [], event_id: g.event_id || "" });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setForm(emptyForm); };

  const toggleMember = (id) => setForm((f) => ({
    ...f,
    member_ids: f.member_ids.includes(id) ? f.member_ids.filter((m) => m !== id) : [...f.member_ids, id],
  }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Inserisci il nome del gruppo"); return; }
    if (!form.category.trim()) { toast.error("Inserisci la categoria"); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(), group_type: form.group_type, discipline: form.discipline,
      category: form.category.trim(), member_ids: form.member_ids, event_id: form.event_id || null,
    };
    try {
      if (form.id) { await api.put(`/groups/${form.id}`, payload); toast.success("Gruppo aggiornato"); }
      else { await api.post("/groups", payload); toast.success("Gruppo creato"); }
      closeForm();
      loadGroups();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (g) => {
    if (!window.confirm(`Eliminare il gruppo "${g.name}"? Verranno rimossi anche i suoi punteggi.`)) return;
    try { await api.delete(`/groups/${g.id}`); toast.success("Gruppo eliminato"); loadGroups(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  return (
    <div data-testid="groups-manager">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users2 className="w-5 h-5 text-gold" />
          <h3 className="text-lg font-semibold">Gruppi / Squadre</h3>
          <span className="text-xs text-muted-foreground">(coppia · squadra · combinata)</span>
        </div>
        {isManager && !showForm && (
          <Button data-testid="create-group-btn" onClick={openCreate} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
            <Plus className="w-4 h-4 mr-1.5" /> Nuovo gruppo
          </Button>
        )}
      </div>

      {isManager && showForm && (
        <div className="rounded-xl border border-gold/30 bg-[#0F172A] p-6 mb-6" data-testid="group-form">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold">{form.id ? "Modifica gruppo" : "Nuovo gruppo"}</h4>
            <button onClick={closeForm} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Nome del gruppo *</Label>
              <Input data-testid="group-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Squadra A" className="mt-1.5" />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.group_type} onValueChange={(v) => setForm({ ...form, group_type: v })}>
                <SelectTrigger data-testid="group-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Disciplina</Label>
              <Select value={form.discipline} onValueChange={(v) => setForm({ ...form, discipline: v, member_ids: [] })}>
                <SelectTrigger data-testid="group-discipline-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(DISC_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Categoria *</Label>
              <Input data-testid="group-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Es. Junior" className="mt-1.5" />
            </div>
            <div className="sm:col-span-2">
              <Label>Gara (opzionale)</Label>
              <Select value={form.event_id || "none"} onValueChange={(v) => setForm({ ...form, event_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="group-event-select" className="mt-1.5"><SelectValue placeholder="Nessuna gara" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna gara</SelectItem>
                  {events.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4">
            <Label>Atlete del gruppo ({form.member_ids.length} selezionate)</Label>
            <div className="mt-2 max-h-52 overflow-auto rounded-lg border border-white/10 divide-y divide-white/5" data-testid="group-members-list">
              {eligibleAthletes.length === 0 ? (
                <p className="text-sm text-muted-foreground p-3">Nessuna atleta {DISC_LABEL[form.discipline]} disponibile. Crea prima le iscrizioni.</p>
              ) : eligibleAthletes.map((a) => (
                <label key={a.id} data-testid={`group-member-${a.id}`} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/5">
                  <input type="checkbox" checked={form.member_ids.includes(a.id)} onChange={() => toggleMember(a.id)} className="accent-[color:var(--gold,#D4AF37)] w-4 h-4" />
                  <span className="text-sm">{a.first_name} {a.last_name}</span>
                  <span className="text-xs text-muted-foreground ml-auto">{a.category}{a.club ? ` · ${a.club}` : ""}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <Button data-testid="save-group-btn" onClick={save} disabled={saving} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : form.id ? "Salva modifiche" : "Crea gruppo"}
            </Button>
            <Button onClick={closeForm} variant="outline" className="border-white/15">Annulla</Button>
          </div>
        </div>
      )}

      {groups.length === 0 ? (
        <p className="text-muted-foreground text-sm py-8 text-center border border-dashed border-white/10 rounded-xl">Nessun gruppo creato. {isManager ? "Crea coppie, squadre o combinate per le gare a gruppi." : ""}</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((g) => (
            <div key={g.id} data-testid={`group-card-${g.id}`} className="rounded-xl border border-white/10 bg-[#0F172A] p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className="font-semibold">{g.name}</h4>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-gold/15 text-gold border border-gold/25">{TYPE_LABEL[g.group_type] || g.group_type}</span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full border border-white/15 text-muted-foreground">{DISC_LABEL[g.discipline]}</span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full border border-white/15 text-muted-foreground">{g.category}</span>
                  </div>
                </div>
                {isManager && (
                  <div className="flex gap-1.5 shrink-0">
                    <button data-testid={`edit-group-${g.id}`} onClick={() => openEdit(g)} className="text-muted-foreground hover:text-gold"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`delete-group-${g.id}`} onClick={() => remove(g)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-3">
                {g.members?.length ? g.members.map((m) => m.name).join(", ") : "Nessuna atleta assegnata"}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
